import styles from "./responseBody.module.scss";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { StatusCode } from "../../../types/sharedTypes";
import { StatusImage } from "../../../shared/components/statusImage/statusImage";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import {
  customParse,
  customStringify,
  getStringJson
} from "../../../utils/getStringJson";
import { getTransformTestCaseFieldByResponse } from "../../../utils/testCaseUtils";
import {
  useCallback,
  useMemo
} from "react";
import { CopyIcon } from "../../../shared/components/copyIcon/copyIcon";
import {
  findScenarioExecuteResponse,
  findTestPlanExecuteResponse
} from "../../../store/selectors";

export const ResponseBody = () => {
  const { t } = useTranslation();

  const sharedFontStyle = {
    fontFamily: "Courier",
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "16px"
  }

  const statusCodes = useSelector((state: RootState) => state.mainSlice.statusCodes);
  const selectedVariables = useSelector((state: RootState) => state.mainSlice.selectedVariables);

  const { selectedTestCase, selectedTestCaseTemplate, executeResponse } = useSelector((state: RootState) => state.testCaseTemplateSlice);

  const scenarioExecuteResponse = useSelector((state: RootState) => findScenarioExecuteResponse(state, selectedTestCase.internalId || ""));
  const testPlanExecuteExecuteResponse = useSelector((state: RootState) => findTestPlanExecuteResponse(state, selectedTestCase.internalId || ""));

  const normalizeObject = useCallback((obj: any): any => {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(normalizeObject);
    }

    const sortedKeys = Object.keys(obj).sort();
    const result = {} as any;
    sortedKeys.forEach(key => {
      result[key] = normalizeObject(obj[key]);
    });
    return result;
  }, []);
  
  const expectedStatusCode = useMemo((): StatusCode | null => {
    const findStatus = statusCodes.find(item => item.id === selectedTestCaseTemplate.statusCodeId);
    if (!!findStatus) {
      return findStatus
    } else {
      return null;
    }
  }, [statusCodes, selectedTestCaseTemplate])

  const localExecuteResponse = useMemo(() => {
    if (!!testPlanExecuteExecuteResponse.internalId) {
      return testPlanExecuteExecuteResponse;
    } else if (!!scenarioExecuteResponse.internalId) {
      return scenarioExecuteResponse;
    } else {
      return executeResponse;
    }
  }, [executeResponse, scenarioExecuteResponse, testPlanExecuteExecuteResponse])

  const responseStatusCode = useMemo(() => {
    const findStatus = statusCodes.find(item => item.code === localExecuteResponse.statusCode);
    if (!!findStatus) {
      return findStatus
    } else {
      return undefined;
    }
  }, [statusCodes, localExecuteResponse])

  const responseBody = useMemo(() => {
    let result = "";
    if (!!localExecuteResponse.validationErrorMessage) {
      result = localExecuteResponse.validationErrorMessage + "\n\n";
    }
    try {
      const normObj = normalizeObject(customParse(localExecuteResponse.responseBody));
      result += customStringify(normObj, null, 2);
    } catch (e) {
      result += getStringJson(localExecuteResponse.responseBody);
    }

    return result;
  }, [localExecuteResponse.responseBody, normalizeObject]);

  const expectedResult = useMemo(() => {
    const transformTestCaseFieldByResponse = getTransformTestCaseFieldByResponse(selectedVariables, selectedTestCase.expectedBody, localExecuteResponse.responseBody);
    try {
      const normObj = normalizeObject(customParse(transformTestCaseFieldByResponse));
      return customStringify(normObj, null, 2);
    } catch (e) {
      return getStringJson(transformTestCaseFieldByResponse)
    }
  }, [selectedVariables, selectedTestCase.expectedBody, localExecuteResponse.responseBody, normalizeObject])

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <span className={styles.response}>
          {t("common.response")}
        </span>
        <span className={styles.expected}>
          {t("testCase.expectedResult")}
        </span>
      </div>
      <div className={styles.bodyContainer}>
        <div className={styles.headers}>
          <div className={styles.statusContainer}>
            {localExecuteResponse.statusCode > 0 && (
              <>
                <div className={styles.content}>
                  <div className={styles.statusCode}>
                    <StatusImage statusCodeId={localExecuteResponse.statusCode}
                                 statusCode={localExecuteResponse.statusCode}/>
                    {localExecuteResponse.statusCode} {responseStatusCode?.name}
                  </div>
                  <span>{localExecuteResponse.time}ms</span>
                </div>
                <CopyIcon text={responseBody}/>
              </>
            )}
          </div>
          <div className={styles.statusContainer}>
            {!!selectedTestCase.id && (
              <>
                <div className={styles.content}>
                  <div className={styles.statusCode}>
                    <StatusImage statusCodeId={selectedTestCaseTemplate.statusCodeId}/>
                    {expectedStatusCode?.code} {expectedStatusCode?.name}
                  </div>
                  <span>{`<${selectedTestCaseTemplate.maximumExecutionTimeout * 1000}ms`}</span>
                </div>
                <CopyIcon text={expectedResult}/>
              </>
            )}
          </div>
        </div>
        <div className={styles.background}>
          <div className={styles.item}></div>
          <div className={styles.item}></div>
        </div>
        <ReactDiffViewer newValue={expectedResult}
                         oldValue={responseBody}
                         disableWordDiff={selectedTestCaseTemplate.isJsonSchemaValidation}
                         splitView={true}
                         hideLineNumbers={true}
                         hideMarkers={true}
                         showDiffOnly={false}
                         compareMethod={DiffMethod.WORDS}
                         styles={{
                           variables: {
                             light: {
                               diffViewerColor: '#000000',
                               addedBackground: '#ffffff',
                               addedColor: '#000000',
                               removedBackground: '#ffffff',
                               removedColor: '#000000',
                               wordAddedBackground: '#FBED75',
                               wordRemovedBackground: '#FBED75',
                               emptyLineBackground: (!!localExecuteResponse.responseBody && !selectedTestCaseTemplate.isJsonSchemaValidation) ? '#FBED75' : "#ffffff",
                             },
                           },
                           contentText: sharedFontStyle,
                           wordDiff: sharedFontStyle,
                           wordAdded: sharedFontStyle,
                           wordRemoved: sharedFontStyle,
                           line: {
                             display: "grid",
                             gridTemplateColumns: "48% 52%"
                           },
                           content: {
                             paddingLeft: 17,
                             borderRight: "1px solid #C3C5CA",
                             overflow: "hidden",
                             textOverflow: "ellipsis",
                             ":last-child": {
                               border: "unset"
                             }
                           }
                         }}
        />
      </div>
    </div>
  )
}

